import React from "react";
import { connect } from "react-redux";

import { faGlobe } from "@fortawesome/pro-solid-svg-icons/faGlobe";
import { faUser } from "@fortawesome/pro-solid-svg-icons/faUser";
import { faUsers } from "@fortawesome/pro-solid-svg-icons/faUsers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { defineMessages, useIntl } from "react-intl";

import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";
import { Avatar } from "@mapmycustomers/ui";

import { getOrganizationName } from "@app/store/iam";
import { getTeams, getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import Scope from "@app/types/dashboard/Scope";
import { userDisplayName } from "@app/util/formatters";

import styles from "./ScopeTag.module.scss";

const messages = defineMessages({
  entireOrganization: {
    id: "dashboard.scopeTag.entireOrganization",
    defaultMessage: "Entire organization",
    description: "A tag to show on a scope tag on Dashboard page when org has no name",
  },
});

interface Props {
  className?: string;
  organizationName?: string;
  scope: Scope;
  teams: Team[];
  users: User[];
}

const ScopeTag: React.FC<Props> = ({ className, organizationName, scope, teams, users }) => {
  const intl = useIntl();

  let icon;
  let title;

  if (!scope.userId && !scope.teamId) {
    icon = <FontAwesomeIcon className={styles.icon} icon={faGlobe} />;
    title = organizationName || intl.formatMessage(messages.entireOrganization);
  } else if (!scope.userId) {
    icon = <FontAwesomeIcon className={styles.icon} icon={faUsers} />;
    const team = teams.find(({ id }) => id === scope.teamId);
    title = team?.name ?? String(scope.teamId);
  } else {
    const user = users.find(({ id }) => id === scope.userId);
    icon = user ? (
      <Avatar className={styles.icon} tooltip={false} user={user} />
    ) : (
      <FontAwesomeIcon className={styles.icon} icon={faUser} />
    );
    title = user ? userDisplayName(user) : String(scope.userId);
  }

  return (
    <Tooltip title={title}>
      <div className={cn(styles.container, className)}>
        {icon}
        <div className={styles.title}>{title}</div>
      </div>
    </Tooltip>
  );
};

const mapStateToProps = (state: RootState) => ({
  organization: getOrganizationName(state),
  teams: getTeams(state),
  users: getUsers(state),
});

export default connect(mapStateToProps)(ScopeTag);
